import { Badge } from "@/components/ui/badge";

const expertiseAreas = [
  {
    category: "API Specializations",
    items: [
      { name: "SolidWorks API", level: "Expert", years: "15+" },
      { name: "NXOpen", level: "Expert", years: "12+" },
      { name: "devDept Eyeshot", level: "Expert", years: "10+" },
      { name: "AutoCAD .NET", level: "Expert", years: "14+" },
      { name: "Inventor APIs", level: "Expert", years: "10+" },
      { name: "RhinoCommon APIs", level: "Expert", years: "8+" },
      { name: "Grasshopper with C#", level: "Advanced", years: "5+" }
    ]
  },
  {
    category: "Programming Languages",
    items: [
      { name: "C#", level: "Expert", years: "15+" },
      { name: "VB.NET", level: "Expert", years: "14+" },
      { name: "Python", level: "Advanced", years: "10+" },
      { name: "C++", level: "Advanced", years: "10+" }
    ]
  },
  {
    category: "Industries",
    items: [
      { name: "Automotive", level: "Expert", years: "12+" },
      { name: "Manufacturing", level: "Expert", years: "15+" },
      { name: "Healthcare", level: "Expert", years: "8+" },
      { name: "Oil & Gas", level: "Expert", years: "7+" },
      { name: "IoT", level: "Expert", years: "5+" }
    ]
  }
];

const TechnicalExpertiseSection = () => {
  return (
    <section 
      id="technical-expertise" 
      data-testid="technical-expertise-section"
      className="py-20 lg:py-28 bg-gradient-to-b from-[#F8FAFC] to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            data-testid="expertise-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A]"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Our Technical Expertise
          </h2>
          <p className="mt-4 text-lg text-[#0F172A]/60 max-w-2xl mx-auto">
            Deep specialized knowledge across CAD APIs, programming languages, and industry-specific solutions
          </p>
        </div>

        {/* Expertise Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {expertiseAreas.map((area, index) => (
            <div
              key={index}
              data-testid={`expertise-category-${index}`}
              className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-[#06B6D4]/50 transition-colors"
            >
              <h3 
                className="text-xl font-bold text-[#0F172A] mb-6"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {area.category}
              </h3>
              
              <div className="space-y-4">
                {area.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-[#0F172A] font-medium">{item.name}</span>
                      <Badge 
                        variant="outline"
                        className="whitespace-nowrap text-xs bg-[#06B6D4]/10 text-[#0E7490] border-[#06B6D4]/30"
                      >
                        {item.level}
                      </Badge>
                    </div>
                    <span className="text-sm text-[#0F172A]/50">{item.years} experience</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Data Exchange Section */}
        <div className="mt-16 pt-16 border-t border-slate-200">
          <div className="bg-gradient-to-br from-[#06B6D4]/5 to-[#0E7490]/5 rounded-2xl p-8 border border-[#06B6D4]/30">
            <h3 
              className="text-2xl font-bold text-[#0F172A] mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Data Exchange & CAD Interoperability
            </h3>
            
            <p className="text-[#0F172A]/70 leading-relaxed mb-6">
              Experienced in developing robust CAD data translation solutions across major neutral formats, ensuring high-fidelity geometry and metadata exchange between heterogeneous systems. Skilled in building import/export pipelines, handling topology/geometry healing, and optimizing performance for large assemblies using leading CAD APIs.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-[#0F172A] mb-4">Key Expertise:</h4>
                <ul className="space-y-2 text-sm text-[#0F172A]/70">
                  <li className="flex gap-2">
                    <span className="text-[#06B6D4] font-bold">•</span>
                    <span>STEP (AP203/AP214/AP242), IGES, DXF/DWG, STL, JT, 3MF, DSTV translators</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[#06B6D4] font-bold">•</span>
                    <span>Geometry & topology processing (B-Rep, meshes, PMI, assemblies)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[#06B6D4] font-bold">•</span>
                    <span>Data healing, validation, and tolerance management</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[#06B6D4] font-bold">•</span>
                    <span>High-performance import/export for large-scale models</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-[#0F172A] mb-4">&nbsp;</h4>
                <ul className="space-y-2 text-sm text-[#0F172A]/70">
                  <li className="flex gap-2">
                    <span className="text-[#06B6D4] font-bold">•</span>
                    <span>CAD API integration (Parasolid, ACIS, Open Cascade, proprietary SDKs)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[#06B6D4] font-bold">•</span>
                    <span>Attribute, layer, color & metadata mapping</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[#06B6D4] font-bold">•</span>
                    <span>Automation & batch conversion workflows</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[#06B6D4] font-bold">•</span>
                    <span>Standards compliance & interoperability debugging</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnicalExpertiseSection;
